import type { JSX } from 'solid-js';
import { CiView } from '../components/CiView';
import { t } from '../i18n';
import { openCiLogUrl, useCiRuns } from '../lib/ci';
import { SESSION_TOKEN, useMe } from '../lib/session';
import { createToast } from '../lib/toast';

// CI runs (arc 101 slice 07) — CONTAINER. Owns data fetching (`useMe`/`useCiRuns`),
// token/session handling, and the `viewLog` handler; delegates all rendering to the
// <CiView/> presenter (dashboard file rule: data fetching ≠ rendering, two files).
// Read-only: a run is born from a `workflow_job` webhook, never the dashboard —
// change one line (`runs-on: developerz-2vcpu`) and a job runs on your box. `token`
// is injectable for tests (mirrors Fleet); the router mounts this with no props.

export function Ci(props: { token?: string | null } = {}): JSX.Element {
  // Default to the dev session ONLY when the prop is omitted — an explicit `null`
  // is a signed-out caller (`??` would wrongly replace it with the dev token).
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const me = useMe(token);
  // Live runs: the `ci` topic re-reads `/v1/ci/runs` (`useCiRuns`, a live read);
  // the rows on screen are always that read's answer.
  const runs = useCiRuns(token);
  const rows = () => runs.data ?? [];
  const hasSession = token !== null;

  const { toast, notify, dismiss } = createToast();

  // The presigned url expires fast, so fetch it on click (never cache it). A 404
  // (no landed / expired log) goes through the page toast like every other
  // failure in this console (#924) — `window.alert` was off-design and BLOCKED:
  // the native modal froze the live runs list behind it until a human dismissed it, and it is what stalls a headless `ui-debugger` run.
  const viewLog = (id: string): void => {
    void openCiLogUrl(id, token).catch(() => notify(t('ci.log_unavailable'), 'error'));
  };

  return (
    <CiView
      login={me.data?.github_login ?? null}
      meLoading={me.isLoading}
      hasSession={hasSession}
      runs={runs}
      rows={rows}
      onViewLog={viewLog}
      toast={toast}
      onDismissToast={dismiss}
    />
  );
}
